import { useElements, useStripe } from "@stripe/react-stripe-js";
import { Stripe, StripeElements } from "@stripe/stripe-js";
import { useRouter } from "next/router";
import { useEffect } from "react";
import { PaymentResult } from "../types";
import { SelectablePaymentMethodEnum } from "config/site";
import { useWebTranslation } from "translations";

const useInitStripe = ({
  handleSetupResult,
  handleUrlMatch,
}: {
  handleSetupResult: (result: PaymentResult) => Promise<void>;
  handleUrlMatch: (
    matchPaymentMethod?: SelectablePaymentMethodEnum.Stripe
  ) => void;
}): {
  stripe: Stripe | null;
  elements: StripeElements | null;
} => {
  const { t } = useWebTranslation();
  const { isReady, query } = useRouter();
  const stripe = useStripe();
  const elements = useElements();

  useEffect(() => {
    if (stripe && isReady) {
      if (query.setup_intent_client_secret) {
        handleUrlMatch(SelectablePaymentMethodEnum.Stripe);

        (async () => {
          const result = await stripe?.retrieveSetupIntent(
            query.setup_intent_client_secret as string
          );

          const error = result?.setupIntent?.last_setup_error;

          if (error) {
            const message =
              error.code === "setup_intent_authentication_failure"
                ? t("add_card.error.verify")
                : error?.message;

            await handleSetupResult({
              error: { message },
            });
          } else {
            await handleSetupResult({
              error: null,
              paymentMethodSetupId: result.setupIntent
                ?.payment_method as string,
              paymentMethod: SelectablePaymentMethodEnum.Stripe,
            });
          }
        })();
      } else {
        handleUrlMatch();
      }
    }
  }, [stripe, isReady, query]);

  return { stripe, elements };
};

export default useInitStripe;
